import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Store,
  Scissors,
  Clock,
  Palette,
  Bell,
  Loader2,
  Plus,
  Trash2,
  Send,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Eye,
  CreditCard,
  Rocket,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Pencil,
} from 'lucide-react'
import { Button, Input, Textarea, Label, Card, CardContent, CardHeader, CardTitle, Badge } from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useOnboardingSubmission,
  useOnboardingBarbers,
  useOnboardingServices,
  useOnboardingFeatures,
} from '@/hooks/onboarding'
import {
  useUpdateSubmission,
  useSubmitSubmission,
  useRevertSubmission,
  usePaySubmission,
  useDeploySubmission,
  useRetryDeploy,
  useCreateBarber,
  useUpdateBarber,
  useDeleteBarber,
  useCreateService,
  useUpdateService,
  useDeleteService,
  useUpdateFeatures,
  useUploadLogo,
  useUploadBarberPhoto,
} from '@/hooks/onboarding'
import type { OnboardingBarber, OnboardingService, OnboardingSubmission } from '@/lib/api'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function ShopInfoSection({
  submission,
  onSave,
}: {
  submission: OnboardingSubmission
  onSave: (data: Partial<OnboardingSubmission>) => void
}) {
  const [editing, setEditing] = useState(false)
  const uploadLogo = useUploadLogo(submission.id)
  const [form, setForm] = useState({
    shop_name: submission.shop_name,
    tagline: submission.tagline,
    about_text: submission.about_text,
    address: submission.address,
    phone: submission.phone,
    email: submission.email,
    primary_color: submission.primary_color,
    secondary_color: submission.secondary_color,
    font_family: submission.font_family,
    business_hours_text: submission.business_hours_text,
    social_links: submission.social_links || ({} as Record<string, string>),
  })

  const handleSave = () => {
    onSave(form)
    setEditing(false)
  }

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await uploadLogo.mutateAsync(e.target.files[0])
    }
  }

  if (!editing) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Store className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Shop Info</CardTitle>
              <p className="text-sm text-muted-foreground">Basic details and branding</p>
            </div>
          </div>
          {submission.status === 'draft' && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>{' '}
              <strong>{submission.shop_name}</strong>
            </div>
            <div>
              <span className="text-muted-foreground">Email:</span> {submission.email || '—'}
            </div>
            <div>
              <span className="text-muted-foreground">Phone:</span> {submission.phone || '—'}
            </div>
            <div>
              <span className="text-muted-foreground">Font:</span> {submission.font_family}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Colors:</span>
            <div className="flex gap-2">
              <div
                className="h-8 w-8 rounded-md border"
                style={{ backgroundColor: submission.primary_color }}
                title="Primary"
              />
              <div
                className="h-8 w-8 rounded-md border"
                style={{ backgroundColor: submission.secondary_color }}
                title="Secondary"
              />
            </div>
          </div>
          {submission.tagline && (
            <p className="text-sm italic text-muted-foreground">"{submission.tagline}"</p>
          )}
          {submission.logo_url && (
            <div className="flex flex-col gap-2 pt-2">
              <span className="text-sm text-muted-foreground">Logo:</span>
              <img
                src={submission.logo_url}
                alt="Shop Logo"
                className="h-16 w-auto rounded-md border object-contain"
              />
            </div>
          )}
          {submission.social_links &&
            Object.entries(submission.social_links).filter(([, url]) => url).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {Object.entries(submission.social_links)
                  .filter(([, url]) => url)
                  .map(([platform, url]) => (
                    <Badge key={platform} variant="outline" className="text-xs">
                      {platform.charAt(0).toUpperCase() + platform.slice(1)}: {url}
                    </Badge>
                  ))}
              </div>
            )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-primary/30">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Store className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="text-xl">Edit Shop Info</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Shop Name *</Label>
            <Input
              value={form.shop_name}
              onChange={(e) => setForm((f) => ({ ...f, shop_name: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label>Tagline</Label>
            <Input
              value={form.tagline}
              onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
              placeholder="Your motto..."
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Address</Label>
          <Input
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label>About</Label>
          <Textarea
            value={form.about_text}
            onChange={(e) => setForm((f) => ({ ...f, about_text: e.target.value }))}
            rows={3}
          />
        </div>
        <div className="space-y-2">
          <Label>Shop Logo</Label>
          <div className="flex items-center gap-4">
            {submission.logo_url && (
              <img
                src={submission.logo_url}
                alt="Shop Logo"
                className="h-12 w-auto rounded-md border object-contain"
              />
            )}
            <Input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              disabled={uploadLogo.isPending}
              className="flex-1 cursor-pointer"
            />
            {uploadLogo.isPending && (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="h-4 w-4" /> Primary Color
            </Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.primary_color}
                onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
                className="h-10 w-10 cursor-pointer rounded"
              />
              <Input
                value={form.primary_color}
                onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Secondary Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.secondary_color}
                onChange={(e) => setForm((f) => ({ ...f, secondary_color: e.target.value }))}
                className="h-10 w-10 cursor-pointer rounded"
              />
              <Input
                value={form.secondary_color}
                onChange={(e) => setForm((f) => ({ ...f, secondary_color: e.target.value }))}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Font Family</Label>
            <Input
              value={form.font_family}
              onChange={(e) => setForm((f) => ({ ...f, font_family: e.target.value }))}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Business Hours</Label>
          <Input
            value={form.business_hours_text}
            onChange={(e) => setForm((f) => ({ ...f, business_hours_text: e.target.value }))}
            placeholder="e.g., Mon-Fri 9am-6pm, Sat 9am-3pm"
          />
        </div>
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4" /> Social Links
          </Label>
          <div className="grid grid-cols-2 gap-3">
            {(['instagram', 'facebook', 'twitter', 'tiktok', 'yelp', 'website'] as const).map(
              (platform) => (
                <div key={platform} className="space-y-1">
                  <Label className="text-xs">
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </Label>
                  <Input
                    value={form.social_links[platform] || ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        social_links: { ...f.social_links, [platform]: e.target.value },
                      }))
                    }
                    placeholder={
                      platform === 'website'
                        ? 'https://yourshop.com'
                        : `https://${platform}.com/yourshop`
                    }
                    className="h-9"
                  />
                </div>
              )
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </div>
      </CardContent>
    </Card>
  )
}

function BarberForm({
  initial,
  onSave,
  onCancel,
  isPending,
}: {
  initial?: Partial<OnboardingBarber>
  onSave: (data: Partial<OnboardingBarber>) => void
  onCancel: () => void
  isPending: boolean
}) {
  const uploadPhoto = useUploadBarberPhoto(initial?.submission_id ?? 0)

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && initial?.id && initial?.submission_id) {
      await uploadPhoto.mutateAsync({ barberId: initial.id, file: e.target.files[0] })
    }
  }

  const [form, setForm] = useState({
    first_name: initial?.first_name || '',
    last_name: initial?.last_name || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    bio: initial?.bio || '',
    specialties: initial?.specialties || '',
    years_experience: initial?.years_experience ?? 0,
    instagram_handle: initial?.instagram_handle || '',
  })

  const defaultSchedules = DAYS.map((_, i) => ({
    day_of_week: i,
    start_time: '09:00',
    end_time: '18:00',
    is_active: i < 5, // Mon-Fri active by default
  }))

  const [schedules, setSchedules] = useState(
    initial?.schedules && initial.schedules.length > 0
      ? (initial.schedules
          .sort((a, b) => a.day_of_week - b.day_of_week)
          .map((s) => ({
            day_of_week: s.day_of_week,
            start_time: s.start_time.slice(0, 5),
            end_time: s.end_time.slice(0, 5),
            is_active: s.is_active,
          })) as any)
      : defaultSchedules
  )

  const updateSchedule = (dayIndex: number, field: string, value: string | boolean) => {
    setSchedules((prev: any[]) =>
      prev.map((s: any) => (s.day_of_week === dayIndex ? { ...s, [field]: value } : s))
    )
  }

  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">First Name *</Label>
          <Input
            value={form.first_name}
            onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
            placeholder="First name"
            className="h-9"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Last Name *</Label>
          <Input
            value={form.last_name}
            onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            placeholder="Last name"
            className="h-9"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Email *</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="barber@shop.com"
            className="h-9"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Phone</Label>
          <Input
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+1-555-0100"
            className="h-9"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Years Experience</Label>
          <Input
            type="number"
            value={form.years_experience}
            onChange={(e) =>
              setForm((f) => ({ ...f, years_experience: parseInt(e.target.value) || 0 }))
            }
            className="h-9"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Instagram</Label>
          <Input
            value={form.instagram_handle}
            onChange={(e) => setForm((f) => ({ ...f, instagram_handle: e.target.value }))}
            placeholder="@handle"
            className="h-9"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Specialties (comma-separated)</Label>
        <Input
          value={form.specialties}
          onChange={(e) => setForm((f) => ({ ...f, specialties: e.target.value }))}
          placeholder="Fades, Classic Cuts, Beard Design"
          className="h-9"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Bio</Label>
        <Textarea
          value={form.bio}
          onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
          rows={2}
          placeholder="Short bio..."
        />
      </div>
      {initial?.id && initial?.submission_id && (
        <div className="space-y-1">
          <Label className="text-xs">Photo</Label>
          <div className="flex items-center gap-3">
            {initial?.photo_url && (
              <img
                src={initial.photo_url}
                alt="Barber Photo"
                className="h-10 w-10 rounded-full border object-cover"
              />
            )}
            <Input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              disabled={uploadPhoto.isPending}
              className="h-9 flex-1 cursor-pointer"
            />
            {uploadPhoto.isPending && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>
      )}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Weekly Schedule</Label>
        <div className="space-y-1">
          {schedules.map((s: any) => (
            <div
              key={s.day_of_week}
              className={`flex items-center gap-3 rounded px-3 py-1.5 ${s.is_active ? 'bg-primary/5' : 'bg-muted/50'}`}
            >
              <span className="w-10 text-xs font-medium">{DAYS[s.day_of_week]}</span>
              <Input
                type="time"
                value={s.start_time}
                onChange={(e) => updateSchedule(s.day_of_week, 'start_time', e.target.value)}
                disabled={!s.is_active}
                className="h-7 w-28 text-xs"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="time"
                value={s.end_time}
                onChange={(e) => updateSchedule(s.day_of_week, 'end_time', e.target.value)}
                disabled={!s.is_active}
                className="h-7 w-28 text-xs"
              />
              <Button
                type="button"
                variant={s.is_active ? 'default' : 'outline'}
                size="sm"
                className="h-7 min-w-[50px] text-xs"
                onClick={() => updateSchedule(s.day_of_week, 'is_active', !s.is_active)}
              >
                {s.is_active ? 'On' : 'Off'}
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => onSave({ ...form, schedules })}
          disabled={isPending || !form.first_name || !form.last_name || !form.email}
        >
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </div>
  )
}

function BarberCard({
  barber,
  onUpdate,
  onDelete,
  isDraft,
}: {
  barber: OnboardingBarber
  onUpdate: (data: Partial<OnboardingBarber>) => void
  onDelete: () => void
  isDraft: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)

  if (editing && isDraft) {
    return (
      <BarberForm
        initial={barber}
        onSave={(data) => {
          onUpdate(data)
          setEditing(false)
        }}
        onCancel={() => setEditing(false)}
        isPending={false}
      />
    )
  }

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {barber.photo_url ? (
            <img
              src={barber.photo_url}
              alt={`${barber.first_name} ${barber.last_name}`}
              className="h-10 w-10 rounded-full border object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
              {barber.first_name.charAt(0)}
              {barber.last_name.charAt(0)}
            </div>
          )}
          <div>
            <p className="font-medium">
              {barber.first_name} {barber.last_name}
            </p>
            <p className="text-sm text-muted-foreground">
              {barber.email} · {barber.years_experience}yr exp
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          {isDraft && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {barber.specialties && (
        <div className="flex flex-wrap gap-1">
          {barber.specialties.split(',').map((s, i) => (
            <Badge key={i} variant="outline" className="text-xs">
              {s.trim()}
            </Badge>
          ))}
        </div>
      )}
      {expanded && (
        <div className="space-y-2 pt-2 text-sm">
          <p className="text-muted-foreground">{barber.bio || 'No bio yet.'}</p>
          {barber.instagram_handle && (
            <p className="text-muted-foreground">Instagram: {barber.instagram_handle}</p>
          )}
          <div className="grid grid-cols-7 gap-1 pt-2">
            {barber.schedules
              .sort((a, b) => a.day_of_week - b.day_of_week)
              .map((s) => (
                <div
                  key={s.day_of_week}
                  className={`rounded p-2 text-center text-xs ${s.is_active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}
                >
                  <div className="font-medium">{DAYS[s.day_of_week]}</div>
                  {s.is_active ? (
                    <div>
                      {s.start_time.slice(0, 5)}-{s.end_time.slice(0, 5)}
                    </div>
                  ) : (
                    <div>Off</div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

function BarbersSection({ submissionId, isDraft }: { submissionId: number; isDraft: boolean }) {
  const { data: barbers, isLoading } = useOnboardingBarbers(submissionId)
  const createBarber = useCreateBarber(submissionId)
  const updateBarber = useUpdateBarber(submissionId)
  const deleteBarber = useDeleteBarber(submissionId)
  const [adding, setAdding] = useState(false)

  if (isLoading) return <Skeleton className="h-40 w-full" />

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Scissors className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-xl">Barbers</CardTitle>
            <p className="text-sm text-muted-foreground">
              {barbers?.length || 0} barbers configured
            </p>
          </div>
        </div>
        {isDraft && (
          <Button variant="outline" size="sm" onClick={() => setAdding(!adding)}>
            <Plus className="mr-1 h-4 w-4" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <BarberForm
            onSave={async (data) => {
              await createBarber.mutateAsync(data)
              setAdding(false)
            }}
            onCancel={() => setAdding(false)}
            isPending={createBarber.isPending}
          />
        )}
        {barbers?.map((barber) => (
          <BarberCard
            key={barber.id}
            barber={barber}
            onUpdate={(data) => updateBarber.mutate({ barberId: barber.id, data })}
            onDelete={() => deleteBarber.mutate(barber.id)}
            isDraft={isDraft}
          />
        ))}
      </CardContent>
    </Card>
  )
}

function ServicesSection({ submissionId, isDraft }: { submissionId: number; isDraft: boolean }) {
  const { data: services, isLoading } = useOnboardingServices(submissionId)
  const createService = useCreateService(submissionId)
  const updateService = useUpdateService(submissionId)
  const deleteService = useDeleteService(submissionId)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({
    category_name: '',
    name: '',
    description: '',
    price: '',
    duration_minutes: '',
  })
  const [newService, setNewService] = useState({
    category_name: '',
    name: '',
    description: '',
    price: '',
    duration_minutes: '',
  })

  if (isLoading) return <Skeleton className="h-40 w-full" />

  const grouped = (services || []).reduce<Record<string, OnboardingService[]>>((acc, svc) => {
    ;(acc[svc.category_name] ||= []).push(svc)
    return acc
  }, {})

  const categories = Object.keys(grouped)

  const handleAddService = async () => {
    if (
      !newService.name ||
      !newService.category_name ||
      !newService.price ||
      !newService.duration_minutes
    )
      return
    await createService.mutateAsync({
      category_name: newService.category_name,
      name: newService.name,
      description: newService.description,
      price: newService.price,
      duration_minutes: parseInt(newService.duration_minutes),
    } as Partial<OnboardingService>)
    setNewService({ category_name: '', name: '', description: '', price: '', duration_minutes: '' })
    setAdding(false)
  }

  const startEditing = (svc: OnboardingService) => {
    setEditingId(svc.id)
    setEditForm({
      category_name: svc.category_name,
      name: svc.name,
      description: svc.description || '',
      price: svc.price,
      duration_minutes: String(svc.duration_minutes),
    })
  }

  const handleEditService = async () => {
    if (editingId === null) return
    if (!editForm.name || !editForm.category_name || !editForm.price || !editForm.duration_minutes)
      return
    await updateService.mutateAsync({
      serviceId: editingId,
      data: {
        category_name: editForm.category_name,
        name: editForm.name,
        description: editForm.description,
        price: editForm.price,
        duration_minutes: parseInt(editForm.duration_minutes),
      } as Partial<OnboardingService>,
    })
    setEditingId(null)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Clock className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-xl">Services</CardTitle>
            <p className="text-sm text-muted-foreground">
              {services?.length || 0} services in {categories.length} categories
            </p>
          </div>
        </div>
        {isDraft && (
          <Button variant="outline" size="sm" onClick={() => setAdding(!adding)}>
            <Plus className="mr-1 h-4 w-4" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {adding && (
          <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Category *</Label>
                <Input
                  value={newService.category_name}
                  onChange={(e) => setNewService((s) => ({ ...s, category_name: e.target.value }))}
                  placeholder="e.g., Haircuts"
                  list="categories"
                  className="h-9"
                />
                <datalist id="categories">
                  {categories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Service Name *</Label>
                <Input
                  value={newService.name}
                  onChange={(e) => setNewService((s) => ({ ...s, name: e.target.value }))}
                  placeholder="e.g., Premium Fade"
                  className="h-9"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Price ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newService.price}
                  onChange={(e) => setNewService((s) => ({ ...s, price: e.target.value }))}
                  placeholder="35.00"
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Duration (min) *</Label>
                <Input
                  type="number"
                  value={newService.duration_minutes}
                  onChange={(e) =>
                    setNewService((s) => ({ ...s, duration_minutes: e.target.value }))
                  }
                  placeholder="30"
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  value={newService.description}
                  onChange={(e) => setNewService((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Optional"
                  className="h-9"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAdding(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleAddService} disabled={createService.isPending}>
                {createService.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Add Service'
                )}
              </Button>
            </div>
          </div>
        )}
        {Object.entries(grouped).map(([category, categoryServices]) => (
          <div key={category}>
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">{category}</h4>
            <div className="space-y-1">
              {categoryServices.map((svc) =>
                editingId === svc.id ? (
                  <div key={svc.id} className="space-y-3 rounded-lg border bg-muted/30 p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Category *</Label>
                        <Input
                          value={editForm.category_name}
                          onChange={(e) =>
                            setEditForm((s) => ({ ...s, category_name: e.target.value }))
                          }
                          placeholder="e.g., Haircuts"
                          list="categories"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Service Name *</Label>
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))}
                          placeholder="e.g., Premium Fade"
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Price ($) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm.price}
                          onChange={(e) => setEditForm((s) => ({ ...s, price: e.target.value }))}
                          placeholder="35.00"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Duration (min) *</Label>
                        <Input
                          type="number"
                          value={editForm.duration_minutes}
                          onChange={(e) =>
                            setEditForm((s) => ({ ...s, duration_minutes: e.target.value }))
                          }
                          placeholder="30"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm((s) => ({ ...s, description: e.target.value }))
                          }
                          placeholder="Optional"
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleEditService}
                        disabled={updateService.isPending}
                      >
                        {updateService.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={svc.id}
                    className="group flex items-center justify-between rounded px-3 py-2 hover:bg-muted/50"
                  >
                    <div>
                      <span className="text-sm font-medium">{svc.name}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {svc.duration_minutes}min
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">${svc.price}</span>
                      {isDraft && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditing(svc)}
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteService.mutate(svc.id)}
                            className="h-6 w-6 p-0 text-destructive opacity-0 hover:text-destructive group-hover:opacity-100"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function FeaturesSection({ submissionId, isDraft }: { submissionId: number; isDraft: boolean }) {
  const { data: features, isLoading } = useOnboardingFeatures(submissionId)
  const updateFeatures = useUpdateFeatures(submissionId)

  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!features) return null

  const toggles = [
    {
      key: 'enable_notifications' as const,
      label: 'Notifications',
      desc: 'Email & push reminders',
    },
    { key: 'enable_reviews' as const, label: 'Reviews', desc: 'Customer review system' },
    { key: 'enable_analytics' as const, label: 'Analytics', desc: 'Business dashboard' },
    { key: 'enable_waitlist' as const, label: 'Waitlist', desc: 'When fully booked' },
    { key: 'enable_payments' as const, label: 'Payments', desc: 'Stripe integration' },
  ]

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-xl">Features</CardTitle>
            <p className="text-sm text-muted-foreground">Choose what your barbershop needs</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {toggles.map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium">{label}</p>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
            <Button
              variant={features[key] ? 'default' : 'outline'}
              size="sm"
              onClick={() => isDraft && updateFeatures.mutate({ [key]: !features[key] })}
              disabled={!isDraft || updateFeatures.isPending}
              className="min-w-[60px]"
            >
              {features[key] ? 'On' : 'Off'}
            </Button>
          </div>
        ))}
        {features.enable_notifications && (
          <div className="space-y-3 border-t pt-3">
            <p className="text-sm font-medium">Notification Settings</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Remind before (hours)</Label>
                <Input
                  type="number"
                  value={features.default_reminder_hours_before}
                  onChange={(e) =>
                    isDraft &&
                    updateFeatures.mutate({
                      default_reminder_hours_before: parseInt(e.target.value) || 24,
                    })
                  }
                  disabled={!isDraft}
                  className="h-8"
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant={features.email_enabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={() =>
                    isDraft && updateFeatures.mutate({ email_enabled: !features.email_enabled })
                  }
                  disabled={!isDraft}
                  className="w-full"
                >
                  Email {features.email_enabled ? 'On' : 'Off'}
                </Button>
              </div>
              <div className="flex items-end">
                <Button
                  variant={features.push_enabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={() =>
                    isDraft && updateFeatures.mutate({ push_enabled: !features.push_enabled })
                  }
                  disabled={!isDraft}
                  className="w-full"
                >
                  Push {features.push_enabled ? 'On' : 'Off'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function CompletionIndicator({ submission }: { submission: OnboardingSubmission }) {
  const steps = [
    { label: 'Shop Info', done: !!submission.shop_name },
    { label: 'Barbers', done: submission.barbers_count > 0 },
    { label: 'Services', done: submission.services_count > 0 },
    { label: 'Features', done: true },
  ]

  const completedCount = steps.filter((s) => s.done).length

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between pb-3">
          <p className="text-sm font-medium">
            {completedCount} of {steps.length} complete
          </p>
          <Badge
            variant={completedCount === steps.length ? 'default' : 'outline'}
            className={
              completedCount === steps.length ? 'bg-green-600 text-white hover:bg-green-700' : ''
            }
          >
            {completedCount === steps.length ? 'Ready to submit' : 'In progress'}
          </Badge>
        </div>
        <div className="flex gap-1">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`h-2 flex-1 rounded-full ${step.done ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
        <div className="mt-2 flex gap-1">
          {steps.map((step, i) => (
            <div key={i} className="flex-1 text-center text-xs text-muted-foreground">
              {step.done ? (
                <span className="text-primary">
                  <CheckCircle className="mr-0.5 inline h-3 w-3" />
                  {step.label}
                </span>
              ) : (
                step.label
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export default function Onboarding() {
  const { submissionId } = useParams<{ submissionId: string }>()
  const id = Number(submissionId)

  const navigate = useNavigate()
  const { data: submission, isLoading, error } = useOnboardingSubmission(id)
  const updateSubmission = useUpdateSubmission(id)
  const submitSubmission = useSubmitSubmission(id)
  const revertSubmission = useRevertSubmission(id)
  const paySubmission = usePaySubmission(id)
  const deploySubmission = useDeploySubmission(id)
  const retryDeploy = useRetryDeploy(id)
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  const handleSubmit = () => {
    if (!submission) return
    const errors: string[] = []
    if (!submission.shop_name.trim()) errors.push('Shop name is required')
    if (submission.barbers_count === 0) errors.push('Add at least one barber')
    if (submission.services_count === 0) errors.push('Add at least one service')
    setValidationErrors(errors)
    if (errors.length > 0) return
    submitSubmission.mutate()
  }

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl space-y-6 p-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-60 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    )
  }

  if (error || !submission) {
    return (
      <div className="container mx-auto max-w-4xl p-6">
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">Submission not found</CardTitle>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const isDraft = submission.status === 'draft'

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/50">
      <div className="container mx-auto max-w-4xl p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4 py-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" />
                Configure Your Barbershop
              </div>
              <h1 className="text-3xl font-bold">{submission.shop_name}</h1>
              <p className="mt-1 text-muted-foreground">
                Customize everything below, then submit when ready.
              </p>
            </div>
            <Badge variant={isDraft ? 'outline' : 'default'} className="px-3 py-1 text-sm">
              {submission.status}
            </Badge>
          </div>
        </motion.div>

        {/* Progress Indicator */}
        {isDraft && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="pb-2"
          >
            <CompletionIndicator submission={submission} />
          </motion.div>
        )}

        {/* Sections */}
        <div className="space-y-6 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <ShopInfoSection
              submission={submission}
              onSave={(data) => updateSubmission.mutate(data)}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <BarbersSection submissionId={id} isDraft={isDraft} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <ServicesSection submissionId={id} isDraft={isDraft} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <FeaturesSection submissionId={id} isDraft={isDraft} />
          </motion.div>

          {/* Action Buttons — status-dependent */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-3"
          >
            {submission.status === 'draft' && (
              <>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 w-full text-base font-medium"
                  onClick={() => navigate(`/onboarding/${id}/staging`)}
                >
                  <Eye className="mr-2 h-5 w-5" /> Preview & Customize Look
                </Button>
                {validationErrors.length > 0 && (
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="space-y-2 py-4">
                      {validationErrors.map((err, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
                          <p className="text-sm text-red-700">{err}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                <Button
                  size="lg"
                  className="h-14 w-full text-base font-medium"
                  onClick={handleSubmit}
                  disabled={submitSubmission.isPending}
                >
                  {submitSubmission.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" /> Submit Configuration
                    </>
                  )}
                </Button>
              </>
            )}

            {submission.status === 'submitted' && (
              <>
                <Button
                  size="lg"
                  className="h-14 w-full bg-green-600 text-base font-medium hover:bg-green-700"
                  onClick={() => paySubmission.mutate()}
                  disabled={paySubmission.isPending}
                >
                  {paySubmission.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-5 w-5" /> Pay & Continue
                    </>
                  )}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 w-full text-base font-medium"
                  onClick={() => revertSubmission.mutate()}
                  disabled={revertSubmission.isPending}
                >
                  {revertSubmission.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Reverting...
                    </>
                  ) : (
                    <>
                      <Pencil className="mr-2 h-5 w-5" /> Edit Again
                    </>
                  )}
                </Button>
              </>
            )}

            {submission.status === 'paid' && (
              <Button
                size="lg"
                className="h-14 w-full bg-blue-600 text-base font-medium hover:bg-blue-700"
                onClick={() => deploySubmission.mutate()}
                disabled={deploySubmission.isPending}
              >
                {deploySubmission.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Deploying...
                  </>
                ) : (
                  <>
                    <Rocket className="mr-2 h-5 w-5" /> Deploy My Barbershop
                  </>
                )}
              </Button>
            )}

            {submission.status === 'deploying' && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="flex items-center gap-3 py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <p className="font-medium text-blue-700">Deploying your barbershop...</p>
                </CardContent>
              </Card>
            )}

            {submission.status === 'deployed' && submission.deployed_domain && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="flex items-center justify-between py-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-700">Your barbershop is live!</p>
                      <p className="text-sm text-green-600">{submission.deployed_domain}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() =>
                      window.open(`http://${submission.deployed_domain}:5173`, '_blank')
                    }
                  >
                    <ExternalLink className="mr-1 h-4 w-4" /> Visit
                  </Button>
                </CardContent>
              </Card>
            )}

            {submission.status === 'failed' && (
              <div className="space-y-3">
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="flex items-center gap-3 py-4">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-700">Deployment failed</p>
                      {submission.deployment_error && (
                        <p className="text-sm text-red-600">{submission.deployment_error}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <Button
                  size="lg"
                  className="h-14 w-full text-base font-medium"
                  onClick={() => retryDeploy.mutate()}
                  disabled={retryDeploy.isPending}
                >
                  {retryDeploy.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-5 w-5" /> Retry Deploy
                    </>
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  )
}
